// Manuais: página web do manual (prévia), link rastreável, métricas de abertura e agendamento para grupos.
// Permissões: manuais.gerenciar (prévia), manuais.enviar (link), manuais.metricas, manuais.agendar.
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@material-ui/core";
import BarChartIcon from "@material-ui/icons/BarChart";
import ScheduleIcon from "@material-ui/icons/Schedule";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import LinkIcon from "@material-ui/icons/Link";

import suporteApi, { mensagemErro } from "./api";
import { copiar, dataBR } from "./comum";
import { usePermissao } from "./permissoes";

const segundos = (s) => (s == null ? "—" : s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`);

export const DialogMetricas = ({ manual, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!manual) return;
    setD(null);
    suporteApi.get(`/api/manuais/${manual.id}/metricas`).then((r) => setD(r.data)).catch((e) => { toast.error(mensagemErro(e)); onClose(); });
  }, [manual, onClose]);
  if (!manual) return null;
  const r = d && d.resumo;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Aberturas · {manual.titulo}</DialogTitle>
      <DialogContent>
        {!d ? <CircularProgress size={24} /> : !r ? <Typography color="textSecondary">Este manual ainda não foi enviado por link.</Typography> : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {[["Envios", r.links], ["Individuais ao técnico", r.envios_individuais], ["Para grupos", r.envios_grupos],
                ["Técnicos distintos", r.tecnicos_distintos], ["Grupos distintos", r.grupos_distintos],
                ["Taxa de abertura", r.taxa_abertura != null ? `${r.taxa_abertura}%` : "—"], ["Aberturas", r.aberturas],
                ["Visitantes", r.visitantes], ["No celular", r.aberturas_celular], ["No computador", r.aberturas_computador],
                ["Tempo médio", segundos(r.tempo_medio_s)], ["Rolagem média", r.rolagem_media != null ? `${r.rolagem_media}%` : "—"],
                ["PDFs baixados", r.pdfs], ["Vídeos tocados", r.videos]].map(([k, v]) => (
                <Chip key={k} label={<span>{k}: <b>{v}</b></span>} variant="outlined" />
              ))}
            </div>
            <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 6 }}>
              Grupo é um destino: as aberturas no link do grupo mostram quantos visitantes distintos abriram, não quem. Prévias do
              WhatsApp não contam.
            </Typography>
            <Table size="small">
              <TableHead><TableRow><TableCell>Enviado</TableCell><TableCell>Destino</TableCell><TableCell>Por</TableCell>
                <TableCell>Aberturas</TableCell><TableCell>1ª abertura</TableCell><TableCell>Tempo máx.</TableCell><TableCell>Rolagem</TableCell><TableCell>PDF</TableCell></TableRow></TableHead>
              <TableBody>
                {d.destinos.map((x) => (
                  <TableRow key={x.codigo}>
                    <TableCell>{dataBR(x.enviado_em)}</TableCell>
                    <TableCell>{x.destino_tipo === "grupo" ? "👥 " : ""}{x.destino_nome || "—"}{x.tecnico_qra ? ` (QRA ${x.tecnico_qra})` : ""}</TableCell>
                    <TableCell>{x.operador || x.origem}</TableCell>
                    <TableCell>{x.aberturas}{x.visitantes > 1 ? ` (${x.visitantes} pessoas)` : ""}</TableCell>
                    <TableCell>{x.primeira_abertura ? dataBR(x.primeira_abertura) : "não abriu"}</TableCell>
                    <TableCell>{segundos(x.tempo_max_s)}</TableCell>
                    <TableCell>{x.rolagem_max != null ? `${x.rolagem_max}%` : "—"}</TableCell>
                    <TableCell>{x.pdfs || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {d.aparelhos.length > 0 && (
              <Typography variant="caption" component="div" style={{ marginTop: 8 }}>
                Aparelhos: {d.aparelhos.map((a) => `${a.dispositivo}${a.sistema ? ` ${a.sistema}` : ""}${a.navegador ? ` / ${a.navegador}` : ""}: ${a.n}`).join(" · ")}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};

export const DialogAgendar = ({ manual, onClose }) => {
  const [grupos, setGrupos] = useState(null);
  const [marcados, setMarcados] = useState([]);
  const [quando, setQuando] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [agendados, setAgendados] = useState([]);
  const carregarAgendados = () => suporteApi.get("/api/manuais/agendamentos").then((r) =>
    setAgendados(r.data.filter((a) => a.manual_id === manual.id))).catch(() => {});
  useEffect(() => {
    if (!manual) return;
    setMarcados([]);
    setMensagem(manual.mensagem || "");
    const d = new Date(Date.now() + 3600000);
    d.setMinutes(0, 0, 0);
    setQuando(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
    suporteApi.get("/api/manuais/grupos").then((r) => setGrupos(r.data)).catch((e) => toast.error(mensagemErro(e)));
    carregarAgendados();
  }, [manual]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!manual) return null;
  const agendar = async () => {
    try {
      await suporteApi.post(`/api/manuais/${manual.id}/agendamentos`, { quando, mensagem, grupos: marcados });
      toast.success("Envio agendado");
      setMarcados([]);
      carregarAgendados();
    } catch (e) { toast.error(mensagemErro(e)); }
  };
  const acao = async (a, oque) => {
    try {
      await suporteApi.post(`/api/manuais/agendamentos/${a.id}/${oque}`);
      carregarAgendados();
    } catch (e) { toast.error(mensagemErro(e)); }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agendar envio para grupos · {manual.titulo}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="textSecondary" component="div">
          Cada grupo recebe um link próprio do manual (as aberturas ficam separadas por grupo). Só aparecem os grupos
          autorizados em Atendimento → Grupos.
        </Typography>
        {!grupos ? <CircularProgress size={20} /> : !grupos.length ? (
          <Typography variant="body2" style={{ margin: "10px 0" }}>Nenhum grupo autorizado para transmissão.</Typography>
        ) : grupos.map((g) => (
          <FormControlLabel key={g.contact_id} style={{ display: "flex" }} label={`${g.nome}${g.finalidade ? ` — ${g.finalidade}` : ""}`}
            control={<Checkbox size="small" color="primary" checked={marcados.includes(g.contact_id)}
              onChange={(e) => setMarcados(e.target.checked ? [...marcados, g.contact_id] : marcados.filter((x) => x !== g.contact_id))} />} />
        ))}
        <TextField id="ag-quando" type="datetime-local" size="small" variant="outlined" margin="dense" label="Quando" InputLabelProps={{ shrink: true }}
          value={quando} onChange={(e) => setQuando(e.target.value)} />
        <TextField id="ag-msg" size="small" variant="outlined" margin="dense" fullWidth multiline minRows={3} label="Mensagem (o link vai no fim)"
          value={mensagem} onChange={(e) => setMensagem(e.target.value)} />
        {agendados.length > 0 && (
          <>
            <Typography variant="subtitle2" style={{ marginTop: 10 }}>Agendamentos deste manual</Typography>
            {agendados.map((a) => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", borderTop: "1px solid rgba(0,0,0,.08)" }}>
                <Typography variant="body2" style={{ flex: 1 }}>
                  {dataBR(a.quando)} · {a.grupos.map((g) => g.nome).join(", ")} · <b>{a.status.toLowerCase()}</b>
                  {(a.resultado || []).filter((x) => !x.ok).map((x) => ` · falhou: ${x.nome} (${x.detalhe})`).join("")}
                </Typography>
                {a.status === "AGENDADO" && <Button size="small" onClick={() => acao(a, "cancelar")}>Cancelar</Button>}
                {["PARCIAL", "FALHOU"].includes(a.status) && <Button size="small" onClick={() => acao(a, "reenviar-falhas")}>Reenviar falhas</Button>}
              </div>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button color="primary" variant="contained" disabled={!marcados.length || !quando} onClick={agendar}>Agendar</Button>
      </DialogActions>
    </Dialog>
  );
};

// botões no card do manual
export const AcoesManual = ({ manual, onMetricas, onAgendar }) => {
  const podeGerenciar = usePermissao("manuais.gerenciar");
  const podeEnviar = usePermissao("manuais.enviar");
  const podeMetricas = usePermissao("manuais.metricas");
  const podeAgendar = usePermissao("manuais.agendar");
  const previa = async () => {
    try {
      const { data } = await suporteApi.get(`/api/manuais/${manual.id}/previa`);
      window.open(data.link, "_blank", "noopener");
    } catch (e) { toast.error(mensagemErro(e)); }
  };
  const link = async () => {
    try {
      const { data } = await suporteApi.post(`/api/manuais/${manual.id}/link`, {});
      await copiar(data.texto);
      toast.success("Mensagem com link rastreável copiada");
    } catch (e) { toast.error(mensagemErro(e)); }
  };
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {podeGerenciar && <Tooltip title="Ver a página web do manual (prévia, não conta nas métricas)"><IconButton size="small" onClick={previa}><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>}
      {podeEnviar && <Tooltip title="Copiar mensagem com link rastreável (para colar onde quiser)"><IconButton size="small" onClick={link}><LinkIcon fontSize="small" /></IconButton></Tooltip>}
      {podeMetricas && <Tooltip title="Aberturas e métricas"><IconButton size="small" onClick={() => onMetricas(manual)}><BarChartIcon fontSize="small" /></IconButton></Tooltip>}
      {podeAgendar && <Tooltip title="Agendar envio para grupos"><IconButton size="small" onClick={() => onAgendar(manual)}><ScheduleIcon fontSize="small" /></IconButton></Tooltip>}
    </div>
  );
};
